import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Search, Sparkles, Globe, MapPin, Building2, Users, Plus, X, Download, 
  Loader2, ChevronDown, ChevronUp, RefreshCw, Trash2, AlertTriangle,
  CheckCircle2, Package, Lightbulb, TrendingUp, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  previewBatchKeywords,
  runBatchKeywordResearch,
  getBatchKeywordJobs,
  getBatchKeywordJob,
  getKeywordSuggestions,
} from "../lib/api";

const EXPANSION_OPTIONS = [
  { id: "us_states", name: "US States", count: 51, icon: MapPin, color: "text-blue-400" },
  { id: "countries", name: "Countries", count: 47, icon: Globe, color: "text-green-400" },
  { id: "cities", name: "US Cities", count: 36, icon: Building2, color: "text-purple-400" },
  { id: "industries", name: "Industries", count: 50, icon: Sparkles, color: "text-yellow-400" },
  { id: "custom", name: "Custom", count: 0, icon: Users, color: "text-pink-400" },
];

const SuggestionCard = ({ suggestion, onAdd, isAdded }) => {
  const volumeColor = suggestion.search_volume > 50000 ? "text-emerald-400" :
                      suggestion.search_volume > 10000 ? "text-yellow-400" : "text-zinc-300";
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
      isAdded 
        ? "bg-emerald-500/10 border-emerald-500/30" 
        : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
    }`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-zinc-100 truncate">{suggestion.keyword}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className={`text-sm font-medium ${volumeColor}`}>
            {suggestion.search_volume.toLocaleString()}/mo
          </span>
          <span className="text-xs text-zinc-500">
            ${suggestion.cpc?.toFixed(2) || "0.00"} CPC
          </span>
          {suggestion.insight && (
            <span className="text-xs text-zinc-400">{suggestion.insight}</span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant={isAdded ? "secondary" : "outline"}
        onClick={() => onAdd(suggestion)}
        disabled={isAdded}
        className="ml-3 shrink-0"
      >
        {isAdded ? (
          <><CheckCircle2 className="h-4 w-4 mr-1" /> Added</>
        ) : (
          <><Plus className="h-4 w-4 mr-1" /> Add</>
        )}
      </Button>
    </div>
  );
};

const BatchCard = ({ batch, index, onRemove }) => {
  const option = EXPANSION_OPTIONS.find(o => o.id === batch.expansion_type);
  const Icon = option?.icon || Package;
  
  return (
    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-md bg-zinc-700 ${option?.color || "text-zinc-400"}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="font-medium text-zinc-100">{batch.base_keyword}</p>
          <p className="text-sm text-zinc-500">
            + {option?.name || batch.expansion_type} ({batch.count} keywords)
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline">{batch.count}</Badge>
        <Button 
          size="icon" 
          variant="ghost" 
          onClick={() => onRemove(index)}
          className="h-8 w-8 text-zinc-500 hover:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const ResultsTable = ({ results, loading }) => {
  const [sortField, setSortField] = useState("search_volume");
  const [sortDir, setSortDir] = useState("desc");
  const [filterSource, setFilterSource] = useState("all");
  const [filterType, setFilterType] = useState("all");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500">
        No results yet. Run a batch research to see data here.
      </div>
    );
  }

  const sources = [...new Set(results.map(r => r.source))];
  const types = [...new Set(results.map(r => r.type))];
  
  let filteredResults = results;
  if (filterSource !== "all") {
    filteredResults = filteredResults.filter(r => r.source === filterSource);
  }
  if (filterType !== "all") {
    filteredResults = filteredResults.filter(r => r.type === filterType);
  }

  const sortedResults = [...filteredResults].sort((a, b) => {
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortDir === "desc" ? bVal - aVal : aVal - bVal;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "desc" ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-500">Source:</span>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources ({results.length})</SelectItem>
              {sources.map(source => (
                <SelectItem key={source} value={source}>
                  {source} ({results.filter(r => r.source === source).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-500">Type:</span>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {types.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Badge variant="secondary" className="ml-auto">
          {sortedResults.length} results
        </Badge>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Keyword</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Source</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-zinc-400">Type</th>
              <th 
                className="text-right py-3 px-4 text-sm font-medium text-zinc-400 cursor-pointer hover:text-zinc-200"
                onClick={() => toggleSort("search_volume")}
              >
                <div className="flex items-center justify-end gap-1">
                  Volume <SortIcon field="search_volume" />
                </div>
              </th>
              <th 
                className="text-right py-3 px-4 text-sm font-medium text-zinc-400 cursor-pointer hover:text-zinc-200"
                onClick={() => toggleSort("cpc")}
              >
                <div className="flex items-center justify-end gap-1">
                  CPC <SortIcon field="cpc" />
                </div>
              </th>
              <th className="text-center py-3 px-4 text-sm font-medium text-zinc-400">Competition</th>
            </tr>
          </thead>
          <tbody>
            {sortedResults.slice(0, 200).map((result, idx) => (
              <tr key={idx} className="border-t border-zinc-800/50 hover:bg-zinc-800/30">
                <td className="py-3 px-4">
                  <span className="text-zinc-100">{result.keyword}</span>
                </td>
                <td className="py-3 px-4">
                  <Badge variant="outline" className="text-xs">{result.source}</Badge>
                </td>
                <td className="py-3 px-4">
                  <span className="text-zinc-400 text-sm">{result.type}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className={`font-medium ${
                    result.search_volume > 10000 ? "text-emerald-400" : 
                    result.search_volume > 1000 ? "text-yellow-400" : 
                    result.search_volume > 0 ? "text-zinc-300" : "text-zinc-600"
                  }`}>
                    {result.search_volume?.toLocaleString() || 0}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className="text-zinc-300">${result.cpc?.toFixed(2) || "0.00"}</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <Badge 
                    variant={
                      result.competition === "HIGH" ? "destructive" : 
                      result.competition === "MEDIUM" ? "warning" : 
                      "secondary"
                    }
                    className="text-xs"
                  >
                    {result.competition || "N/A"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedResults.length > 200 && (
          <div className="text-center py-4 text-sm text-zinc-500 bg-zinc-800/30">
            Showing 200 of {sortedResults.length} results. Export CSV to see all.
          </div>
        )}
      </div>
    </div>
  );
};

export default function KeywordResearch() {
  // Batch builder state
  const [baseKeyword, setBaseKeyword] = useState("");
  const [expansionType, setExpansionType] = useState("us_states");
  const [customItems, setCustomItems] = useState("");
  const [batches, setBatches] = useState([]);
  
  // Bulk import state
  const [bulkKeywords, setBulkKeywords] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedExpansions, setSelectedExpansions] = useState(["us_states"]); // Multi-select for bulk
  
  // Smart suggestions state
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [addedSuggestions, setAddedSuggestions] = useState(new Set());
  
  // Job state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  
  const [activeTab, setActiveTab] = useState("builder");

  // Parse bulk keywords - handles newlines, commas, tabs, semicolons
  const parseBulkKeywords = (text) => {
    return text
      .split(/[\n,;\t]+/)
      .map(k => k.trim())
      .filter(k => k.length > 0 && k.length < 100);
  };

  const bulkKeywordCount = parseBulkKeywords(bulkKeywords).length;

  // Calculate total expansion count for multi-select
  const getMultiExpansionCount = () => {
    return selectedExpansions.reduce((sum, expId) => {
      if (expId === "custom") {
        return sum + customItems.split("\n").filter(i => i.trim()).length;
      }
      const opt = EXPANSION_OPTIONS.find(o => o.id === expId);
      return sum + (opt?.count || 0);
    }, 0);
  };

  const multiExpansionCount = getMultiExpansionCount();
  const bulkTotalKeywords = bulkKeywordCount * multiExpansionCount;

  // Toggle expansion selection
  const toggleExpansion = (expId) => {
    if (expId === "custom") {
      // Custom is exclusive - selecting it deselects others
      setSelectedExpansions(selectedExpansions.includes("custom") ? [] : ["custom"]);
    } else {
      // Remove custom if selecting a preset
      const withoutCustom = selectedExpansions.filter(e => e !== "custom");
      if (withoutCustom.includes(expId)) {
        setSelectedExpansions(withoutCustom.filter(e => e !== expId));
      } else {
        setSelectedExpansions([...withoutCustom, expId]);
      }
    }
  };

  // Select all preset expansions
  const selectAllExpansions = () => {
    setSelectedExpansions(["us_states", "countries", "cities", "industries"]);
  };

  // Calculate totals
  const totalKeywords = batches.reduce((sum, b) => sum + b.count, 0);
  const progressPercent = Math.min((totalKeywords / 1000) * 100, 100);
  const apiCallsNeeded = Math.ceil(totalKeywords / 1000);
  const estimatedCost = apiCallsNeeded * 0.075;

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await getBatchKeywordJobs(20);
      setJobs(response.data);
    } catch (error) {
      console.error("Failed to load jobs:", error);
    }
  };

  const getExpansionCount = (type) => {
    if (type === "custom") {
      return customItems.split("\n").filter(i => i.trim()).length;
    }
    return EXPANSION_OPTIONS.find(o => o.id === type)?.count || 0;
  };

  // Smart Suggestions handlers
  const handleGetSuggestions = async () => {
    if (!baseKeyword.trim()) {
      toast.error("Please enter a base keyword first");
      return;
    }
    
    setSuggestionsLoading(true);
    setSuggestions(null);
    
    try {
      const response = await getKeywordSuggestions(baseKeyword.trim(), 10);
      setSuggestions(response.data);
      
      if (response.data.suggestions?.length > 0) {
        toast.success(`Found ${response.data.suggestions.length} high-value alternatives`);
      } else {
        toast.info("No high-volume alternatives found for this keyword");
      }
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("DataForSEO account needs funds. Add balance to continue.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to get suggestions");
      }
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleAddSuggestion = (suggestion) => {
    // Add suggestion as a new base keyword (will be added to batch with current expansion type)
    const newBatch = {
      base_keyword: suggestion.keyword,
      expansion_type: expansionType,
      custom_items: expansionType === "custom" 
        ? customItems.split("\n").filter(i => i.trim())
        : [],
      count: getExpansionCount(expansionType)
    };
    
    setBatches([...batches, newBatch]);
    setAddedSuggestions(new Set([...addedSuggestions, suggestion.keyword.toLowerCase()]));
    toast.success(`Added "${suggestion.keyword}" to batch`);
  };

  const handleAddAllHighValue = () => {
    if (!suggestions?.suggestions) return;
    
    // Add all suggestions with volume higher than seed
    const highValue = suggestions.suggestions.filter(s => 
      s.search_volume >= (suggestions.seed_volume || 0) &&
      !addedSuggestions.has(s.keyword.toLowerCase())
    );
    
    const newBatches = highValue.map(s => ({
      base_keyword: s.keyword,
      expansion_type: expansionType,
      custom_items: expansionType === "custom" 
        ? customItems.split("\n").filter(i => i.trim())
        : [],
      count: getExpansionCount(expansionType)
    }));
    
    setBatches([...batches, ...newBatches]);
    setAddedSuggestions(new Set([
      ...addedSuggestions, 
      ...highValue.map(s => s.keyword.toLowerCase())
    ]));
    toast.success(`Added ${highValue.length} high-value keywords`);
  };

  const addBatch = () => {
    if (!baseKeyword.trim()) {
      toast.error("Please enter a base keyword");
      return;
    }
    
    const count = getExpansionCount(expansionType);
    if (count === 0) {
      toast.error("Please select an expansion type or add custom items");
      return;
    }

    const newBatch = {
      base_keyword: baseKeyword.trim(),
      expansion_type: expansionType,
      custom_items: expansionType === "custom" 
        ? customItems.split("\n").filter(i => i.trim())
        : [],
      count: count
    };

    setBatches([...batches, newBatch]);
    setBaseKeyword("");
    setSuggestions(null);
    setAddedSuggestions(new Set());
    toast.success(`Added "${newBatch.base_keyword}" + ${count} keywords`);
  };

  // Bulk import handler - adds multiple keywords at once with multi-expansion
  const handleBulkImport = () => {
    const keywords = parseBulkKeywords(bulkKeywords);
    
    if (keywords.length === 0) {
      toast.error("No valid keywords found. Paste keywords separated by newlines, commas, or tabs.");
      return;
    }

    if (selectedExpansions.length === 0) {
      toast.error("Please select at least one expansion type");
      return;
    }

    const customList = customItems.split("\n").filter(i => i.trim());
    
    // Create batches for each keyword + each expansion type
    const newBatches = [];
    
    for (const kw of keywords) {
      for (const expType of selectedExpansions) {
        const count = expType === "custom" 
          ? customList.length 
          : EXPANSION_OPTIONS.find(o => o.id === expType)?.count || 0;
        
        if (count > 0) {
          newBatches.push({
            base_keyword: kw,
            expansion_type: expType,
            custom_items: expType === "custom" ? customList : [],
            count: count
          });
        }
      }
    }
    
    setBatches([...batches, ...newBatches]);
    const totalAdded = newBatches.reduce((sum, b) => sum + b.count, 0);
    toast.success(`Added ${keywords.length} keywords × ${selectedExpansions.length} expansions = ${totalAdded} total keywords`);
    
    setBulkKeywords("");
    setShowBulkImport(false);
  };

  // Add keywords without expansion (direct research)
  const handleBulkImportDirect = () => {
    const keywords = parseBulkKeywords(bulkKeywords);
    
    if (keywords.length === 0) {
      toast.error("No valid keywords found");
      return;
    }

    // Add as single batch with "none" expansion type
    const newBatches = keywords.map(kw => ({
      base_keyword: kw,
      expansion_type: "none",
      custom_items: [],
      count: 1
    }));
    
    setBatches([...batches, ...newBatches]);
    setBulkKeywords("");
    setShowBulkImport(false);
    toast.success(`Added ${keywords.length} keywords for direct research`);
  };

  const removeBatch = (index) => {
    setBatches(batches.filter((_, i) => i !== index));
  };

  const clearAllBatches = () => {
    setBatches([]);
    toast.success("All batches cleared");
  };

  const handleRunResearch = async () => {
    if (batches.length === 0) {
      toast.error("Add at least one batch to research");
      return;
    }

    setResearchLoading(true);
    try {
      const response = await runBatchKeywordResearch(batches);
      toast.success(`Research started! ${response.data.unique_keywords} unique keywords`);
      
      // Switch to history and poll for results
      setActiveTab("history");
      loadJobs();
      pollJobStatus(response.data.job_id);
      
      // Clear batches after successful start
      setBatches([]);
      setSuggestions(null);
      setAddedSuggestions(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start research");
    } finally {
      setResearchLoading(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    const checkStatus = async () => {
      try {
        const response = await getBatchKeywordJob(jobId);
        const job = response.data;
        
        if (job.status === "completed") {
          toast.success(`Research completed! ${job.results?.length || 0} keywords with data`);
          setSelectedJob(job);
          loadJobs();
        } else if (job.status === "failed") {
          toast.error(`Research failed: ${job.error_message}`);
          loadJobs();
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error("Failed to check job status:", error);
      }
    };
    
    checkStatus();
  };

  const loadJobDetails = async (jobId) => {
    setJobLoading(true);
    try {
      const response = await getBatchKeywordJob(jobId);
      setSelectedJob(response.data);
    } catch (error) {
      toast.error("Failed to load job details");
    } finally {
      setJobLoading(false);
    }
  };

  const handleExport = (jobId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/keywords/batch/jobs/${jobId}/export?format=csv`;
    window.open(url, "_blank");
  };

  // Count high-value suggestions (volume >= seed)
  const highValueCount = suggestions?.suggestions?.filter(
    s => s.search_volume >= (suggestions.seed_volume || 0) && !addedSuggestions.has(s.keyword.toLowerCase())
  ).length || 0;

  return (
    <div className="space-y-6" data-testid="keyword-research-page">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100">Keyword Research</h1>
        <p className="text-zinc-500 mt-1">Build keyword batches and research up to 1000 keywords per API call ($0.075)</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="builder" data-testid="builder-tab">
            <Package className="h-4 w-4 mr-2" />
            Batch Builder
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            History ({jobs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-6">
          {/* Bulk Import Toggle */}
          <div className="flex justify-end">
            <Button
              variant={showBulkImport ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowBulkImport(!showBulkImport)}
              data-testid="toggle-bulk-import"
            >
              <Package className="h-4 w-4 mr-2" />
              {showBulkImport ? "Hide Bulk Import" : "Bulk Import (Paste Multiple Keywords)"}
            </Button>
          </div>

          {/* Bulk Import Panel */}
          {showBulkImport && (
            <Card className="bg-zinc-900/50 border-emerald-500/30 border-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-emerald-500" />
                  Bulk Import Keywords
                </CardTitle>
                <CardDescription>
                  Paste multiple keywords separated by newlines, commas, tabs, or semicolons
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">
                    Paste your keywords ({bulkKeywordCount} detected)
                  </label>
                  <textarea
                    value={bulkKeywords}
                    onChange={(e) => setBulkKeywords(e.target.value)}
                    placeholder="google ads for plumbers&#10;google ads for lawyers&#10;google ads for dentists&#10;&#10;Or paste comma-separated: keyword1, keyword2, keyword3"
                    className="w-full h-48 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-sm font-mono"
                    data-testid="bulk-keywords-input"
                  />
                  {bulkKeywordCount > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      <span className="text-emerald-400">{bulkKeywordCount} keywords detected</span>
                    </div>
                  )}
                </div>

                {/* Preview parsed keywords */}
                {bulkKeywordCount > 0 && bulkKeywordCount <= 20 && (
                  <div className="p-3 bg-zinc-800/50 rounded-md">
                    <p className="text-xs text-zinc-500 mb-2">Preview:</p>
                    <div className="flex flex-wrap gap-1">
                      {parseBulkKeywords(bulkKeywords).slice(0, 20).map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {bulkKeywordCount > 20 && (
                  <div className="p-3 bg-zinc-800/50 rounded-md">
                    <p className="text-xs text-zinc-500 mb-2">Preview (first 20 of {bulkKeywordCount}):</p>
                    <div className="flex flex-wrap gap-1">
                      {parseBulkKeywords(bulkKeywords).slice(0, 20).map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {kw}
                        </Badge>
                      ))}
                      <Badge variant="outline" className="text-xs">+{bulkKeywordCount - 20} more</Badge>
                    </div>
                  </div>
                )}

                {/* Multi-Select Expansion Types */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-zinc-400">Select Expansions (multi-select)</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={selectAllExpansions}
                      className="text-xs h-7"
                      data-testid="select-all-expansions"
                    >
                      Select All ({51 + 47 + 36 + 50} = 184)
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {EXPANSION_OPTIONS.filter(o => o.id !== "custom").map(opt => {
                      const isSelected = selectedExpansions.includes(opt.id);
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => toggleExpansion(opt.id)}
                          className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${
                            isSelected 
                              ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                              : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600 text-zinc-400"
                          }`}
                          data-testid={`expansion-${opt.id}`}
                        >
                          <Icon className={`h-4 w-4 ${isSelected ? "text-emerald-400" : opt.color}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isSelected ? "text-emerald-300" : "text-zinc-200"}`}>
                              {opt.name}
                            </p>
                            <p className="text-xs">{opt.count} items</p>
                          </div>
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                        </button>
                      );
                    })}
                  </div>

                  {/* Summary */}
                  {selectedExpansions.length > 0 && bulkKeywordCount > 0 && (
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-300">
                            {bulkKeywordCount} keywords × {multiExpansionCount} expansions
                          </p>
                          <p className="text-xs text-emerald-400/80">
                            Selected: {selectedExpansions.map(e => EXPANSION_OPTIONS.find(o => o.id === e)?.name).join(" + ")}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-400">{bulkTotalKeywords.toLocaleString()}</p>
                          <p className="text-xs text-emerald-400/80">total keywords</p>
                        </div>
                      </div>
                      {bulkTotalKeywords > 1000 && (
                        <div className="flex items-center gap-2 mt-2 text-yellow-400 text-xs">
                          <AlertTriangle className="h-3 w-3" />
                          <span>
                            Exceeds 1000 limit. Will use {Math.ceil(bulkTotalKeywords / 1000)} API calls (${(Math.ceil(bulkTotalKeywords / 1000) * 0.075).toFixed(3)})
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Import options */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <Button
                    onClick={handleBulkImportDirect}
                    disabled={bulkKeywordCount === 0}
                    variant="outline"
                    className="h-auto py-3"
                    data-testid="bulk-import-direct"
                  >
                    <div className="text-left">
                      <div className="flex items-center gap-2 font-medium">
                        <Search className="h-4 w-4" />
                        Research As-Is
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {bulkKeywordCount} keywords → {bulkKeywordCount} API lookups
                      </p>
                    </div>
                  </Button>
                  
                  <Button
                    onClick={handleBulkImport}
                    disabled={bulkKeywordCount === 0 || selectedExpansions.length === 0}
                    className="h-auto py-3 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="bulk-import-expand"
                  >
                    <div className="text-left">
                      <div className="flex items-center gap-2 font-medium">
                        <Sparkles className="h-4 w-4" />
                        Import + Expand
                      </div>
                      <p className="text-xs text-zinc-200/70 mt-1">
                        {bulkKeywordCount} × {multiExpansionCount} = {bulkTotalKeywords.toLocaleString()} keywords
                      </p>
                    </div>
                  </Button>
                </div>

                <p className="text-xs text-zinc-500">
                  Tip: Select multiple expansions to maximize the 1000 keyword limit per API call. 
                  Example: 5 keywords × (States + Industries) = 5 × 101 = 505 keywords
                </p>
              </CardContent>
            </Card>
          )}

          {/* Keyword Builder with Smart Suggestions */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5 text-emerald-500" />
                Add Keywords to Batch
              </CardTitle>
              <CardDescription>
                Enter a base keyword and get smart suggestions for high-value alternatives
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Base Keyword + Get Suggestions */}
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Base Keyword</label>
                <div className="flex gap-2">
                  <Input
                    value={baseKeyword}
                    onChange={(e) => {
                      setBaseKeyword(e.target.value);
                      setSuggestions(null);
                      setAddedSuggestions(new Set());
                    }}
                    placeholder="e.g., Google ads, SEO services"
                    className="bg-zinc-800 border-zinc-700 flex-1"
                    data-testid="base-keyword-input"
                    onKeyPress={(e) => e.key === "Enter" && handleGetSuggestions()}
                  />
                  <Button
                    onClick={handleGetSuggestions}
                    variant="outline"
                    disabled={!baseKeyword.trim() || suggestionsLoading}
                    className="shrink-0"
                    data-testid="get-suggestions-btn"
                  >
                    {suggestionsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Lightbulb className="h-4 w-4 mr-2" /> Get Suggestions</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Smart Suggestions Panel */}
              {suggestions && (
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-amber-400" />
                      <h4 className="font-medium text-zinc-100">High-Value Alternatives</h4>
                    </div>
                    {suggestions.api_cost && (
                      <Badge variant="outline" className="text-xs">
                        API cost: ${suggestions.api_cost.toFixed(3)}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Seed keyword info */}
                  {suggestions.seed_data && (
                    <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded-md">
                      <Badge className="bg-emerald-600">Your keyword</Badge>
                      <span className="text-zinc-100">{suggestions.seed_keyword}</span>
                      <span className="text-emerald-400 font-medium">
                        {suggestions.seed_volume?.toLocaleString()}/mo
                      </span>
                    </div>
                  )}
                  
                  {/* Suggestions list */}
                  {suggestions.suggestions?.length > 0 ? (
                    <>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {suggestions.suggestions.map((s, idx) => (
                          <SuggestionCard
                            key={idx}
                            suggestion={s}
                            onAdd={handleAddSuggestion}
                            isAdded={addedSuggestions.has(s.keyword.toLowerCase())}
                          />
                        ))}
                      </div>
                      
                      {/* Bulk actions */}
                      <div className="flex gap-2 pt-2 border-t border-zinc-800">
                        {highValueCount > 0 && (
                          <Button
                            onClick={handleAddAllHighValue}
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add All High-Value ({highValueCount})
                          </Button>
                        )}
                        <Button
                          onClick={() => {
                            setSuggestions(null);
                            setAddedSuggestions(new Set());
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          Skip Suggestions
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-zinc-500">
                      No high-volume alternatives found. Your keyword may already be optimal!
                    </p>
                  )}
                </div>
              )}

              {/* Expansion Type */}
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Expansion Type</label>
                <Select value={expansionType} onValueChange={setExpansionType}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPANSION_OPTIONS.map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <div className="flex items-center gap-2">
                          <opt.icon className={`h-4 w-4 ${opt.color}`} />
                          {opt.name} {opt.id !== "custom" && `(${opt.count})`}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {expansionType === "custom" && (
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Custom Items (one per line)</label>
                  <textarea
                    value={customItems}
                    onChange={(e) => setCustomItems(e.target.value)}
                    placeholder="plumbers&#10;lawyers&#10;dentists&#10;real estate agents"
                    className="w-full h-32 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-sm"
                    data-testid="custom-items-input"
                  />
                  <p className="text-xs text-zinc-500">
                    {customItems.split("\n").filter(i => i.trim()).length} items
                  </p>
                </div>
              )}

              <Button 
                onClick={addBatch} 
                className="w-full"
                disabled={!baseKeyword.trim() || getExpansionCount(expansionType) === 0}
                data-testid="add-batch-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Batch (+{getExpansionCount(expansionType)} keywords)
              </Button>
            </CardContent>
          </Card>

          {/* Current Batch */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Current Batch</CardTitle>
                  <CardDescription>
                    {totalKeywords} / 1,000 keywords
                  </CardDescription>
                </div>
                {batches.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllBatches}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <Progress value={progressPercent} className="h-3" />
                <div className="flex justify-between text-sm">
                  <span className={totalKeywords > 1000 ? "text-yellow-400" : "text-zinc-500"}>
                    {totalKeywords > 1000 && <AlertTriangle className="h-4 w-4 inline mr-1" />}
                    {totalKeywords.toLocaleString()} keywords
                  </span>
                  <span className="text-zinc-500">
                    {apiCallsNeeded} API call{apiCallsNeeded !== 1 ? "s" : ""} = ${estimatedCost.toFixed(3)}
                  </span>
                </div>
              </div>

              {/* Warning for >1000 */}
              {totalKeywords > 1000 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Batch exceeds 1,000 keywords. This will require {apiCallsNeeded} API calls (${estimatedCost.toFixed(3)} total).</span>
                </div>
              )}

              {/* Batch List */}
              {batches.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No keywords in batch yet.</p>
                  <p className="text-sm">Add base keywords above to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {batches.map((batch, index) => (
                    <BatchCard 
                      key={index} 
                      batch={batch} 
                      index={index}
                      onRemove={removeBatch}
                    />
                  ))}
                </div>
              )}

              {/* Run Research Button */}
              <Button
                onClick={handleRunResearch}
                disabled={batches.length === 0 || researchLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                data-testid="run-research-btn"
              >
                {researchLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Search className="h-5 w-5 mr-2" />
                )}
                Run Research ({totalKeywords.toLocaleString()} keywords) - ${estimatedCost.toFixed(3)}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-zinc-100">Research History</h2>
            <Button onClick={loadJobs} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {jobs.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-12 text-center text-zinc-500">
                No research jobs yet. Build a batch and run research to see results here.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job) => (
                <Card
                  key={job.id}
                  className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all hover:border-zinc-700 ${
                    selectedJob?.id === job.id ? "border-emerald-500" : ""
                  }`}
                  onClick={() => loadJobDetails(job.id)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-zinc-100">
                          {job.batches?.map(b => b.base_keyword).join(", ") || "Batch Research"}
                        </h3>
                        <p className="text-sm text-zinc-500">
                          {job.unique_keywords || job.total_keywords} keywords • {new Date(job.created_at).toLocaleDateString()}
                          {job.actual_cost > 0 && ` • $${job.actual_cost.toFixed(3)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge
                          variant={job.status === "completed" ? "default" : job.status === "processing" ? "warning" : "destructive"}
                          className={job.status === "completed" ? "bg-emerald-600" : ""}
                        >
                          {job.status === "processing" && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          {job.status === "completed" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {job.status}
                        </Badge>
                        {job.status === "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExport(job.id);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {job.batches && job.batches.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {job.batches.map((batch, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {batch.base_keyword} ({batch.count})
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Selected Job Results */}
          {selectedJob && (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Results</CardTitle>
                    <CardDescription>
                      {selectedJob.results?.length || 0} keywords • 
                      {selectedJob.duplicates_removed > 0 && ` ${selectedJob.duplicates_removed} duplicates removed •`}
                      {" "}Cost: ${selectedJob.actual_cost?.toFixed(3) || "0.00"}
                    </CardDescription>
                  </div>
                  {selectedJob.status === "completed" && (
                    <Button onClick={() => handleExport(selectedJob.id)} variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ResultsTable results={selectedJob.results} loading={jobLoading} />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
